import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useDatabaseContent } from '@databases/database-state';
import { useFlags } from '@databases/flags';
import { type AddFlagProps } from '@databases/flags-types';
import { messages as globalMessages } from '@databases/intl/messages';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import { type UndoManager } from 'yjs';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { getMessageByType } from '../helpers/message-helper';
import { messages } from '../messages';
import { type PasteFlagParams, type PasteMessageType } from '../types';

export const useDisplayPasteFlags = (undoManager: UndoManager | undefined) => {
  const { formatMessage } = useIntl();
  const { addFlag, clearFlags, showFlag } = useFlags();
  const client = useAnalytics();
  const { contentId } = useDatabaseContent();

  const sendAnalyticEvent = useCallback(
    async (contentId: string, actionType: PasteMessageType) => {
      await client.sendUndoEvent({
        action: 'paste',
        type: actionType as Exclude<
          PasteMessageType,
          | 'cannotReadPastedContent'
          | 'unexpectedDataType'
          | 'structureLocked'
          | 'readOnly'
          | 'entriesFiltered'
          | 'restrictedContentCantBeCopied'
          | 'pastedIntoSort'
        >,
        source: 'flags',
        contentId,
        databaseContainer: getDatabaseContainerType()
      });
    },
    [client]
  );

  const handleUndo = useCallback(
    (actionType: PasteMessageType, undoManager: UndoManager) => {
      undoManager.undo();
      if (contentId) {
        sendAnalyticEvent(contentId, actionType);
      }
    },
    [contentId, sendAnalyticEvent]
  );

  const handleFlag = useCallback(
    (flag: AddFlagProps) => {
      if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
        showFlag({
          id: `paste-flag-${flag.title}`,
          title: flag.title,
          description: flag.description,
          appearance: flag.appearance,
          actions: flag.actions,
          type: flag.appearance as 'warning' | 'error' | 'info',
          close: 'auto'
        });
      } else {
        addFlag(flag);
      }
    },
    [addFlag, showFlag]
  );

  /**
   * Returns actions for the flag
   *
   * @param undoManager - When defined, the undo action will be added to the actions
   */
  const getActions = useCallback(
    (pasteMessageType: PasteMessageType, undoManager: UndoManager | undefined) => {
      const result = [];
      if (undoManager) {
        result.push({
          content: formatMessage(globalMessages['button-message.undo-action']),
          onClick: () => {
            handleUndo(pasteMessageType, undoManager);
            clearFlags();
          }
        });
      }

      const dismissAction = {
        content: formatMessage(globalMessages['button-message.dismiss-action']),
        onClick: () => {
          clearFlags();
        }
      };
      result.push(dismissAction);
      return result;
    },
    [clearFlags, formatMessage, handleUndo]
  );

  const getFlagData = useCallback(
    (createPasteFlagParams: PasteFlagParams[]): AddFlagProps[] => {
      const result: AddFlagProps[] = [];
      for (const { type, actionType, options } of createPasteFlagParams) {
        const { title, description, excludeUndo } = getMessageByType(actionType);
        const messageValues =
          options && 'messageValues' in options ? options.messageValues : undefined;

        result.push({
          title: formatMessage(messages[title], messageValues),
          description: description
            ? formatMessage(messages[description], messageValues)
            : '',
          appearance: type,
          actions: getActions(actionType, excludeUndo ? undefined : undoManager)
        });
      }

      return result;
    },
    [formatMessage, getActions, undoManager]
  );

  return useCallback(
    (createPasteFlagParams: PasteFlagParams[]) => {
      const addFlagData = getFlagData(createPasteFlagParams);
      for (const flagData of addFlagData) {
        handleFlag(flagData);
      }
    },
    [handleFlag, getFlagData]
  );
};
